/**
 * One session in the history list. Tappable → /tracker/:id. A `live` stub
 * (warmup awaiting its result) renders distinctly with a "Finish" affordance.
 */

import { Link } from "react-router-dom";

import type { LiveSession } from "@/shared/models";
import { shortDate } from "@/shared/format";
import { durationLabel, money, netToneClass } from "@/features/tracker/lib/trackerFormat";

export function SessionRow({ session }: { session: LiveSession }) {
  const isLive = session.status === "live";

  return (
    <Link
      to={`/tracker/${session.id}`}
      className="flex items-start justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-3 transition hover:bg-text/5"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-text truncate">
            {session.title ??
              session.stakes ??
              (session.gameType === "tournament" ? "Tournament" : "Session")}
          </span>
          {isLive && (
            <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
              In progress
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-text-muted truncate">
          {session.title && session.stakes ? `${session.stakes} · ` : ""}
          {shortDate(session.startedAt)}
          {!isLive && ` · ${durationLabel(session.durationMinutes)}`}
          {session.location && ` · ${session.location}`}
        </div>
        {session.notes ? (
          <div className="mt-1.5 text-xs text-text-muted whitespace-pre-line line-clamp-3">
            {session.notes}
          </div>
        ) : session.intention ? (
          <div className="mt-1.5 text-xs text-text-muted/90 italic line-clamp-2">
            “{session.intention}”
          </div>
        ) : null}
      </div>

      <div className="text-right shrink-0">
        {isLive ? (
          <span className="text-sm font-medium text-accent">Finish →</span>
        ) : (
          <span className={`text-base font-semibold tabular-nums ${netToneClass(session.net)}`}>
            {money(session.net)}
          </span>
        )}
      </div>
    </Link>
  );
}
